import { useEffect, useState, useRef } from "react";
import { Calendar } from 'primereact/calendar';
import { Toast } from "primereact/toast";
import Table from "./components/Table";
import { getData, postData, downloadFile } from "./services/api";
import { useSession } from "./assets/auth/SessionContext";
import models from "./utils/models";
import datePicker from "./utils/date-picker";
import dayjs from "dayjs";
import QRCode from "react-qr-code";
import { classNames } from "primereact/utils";
import { CalendarDays, Filter, Mail, Search } from "lucide-react";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import { Checkbox } from "primereact/checkbox";

const formatSoldAt = (raw: any): string => {
    if (!raw) return '';
    try {
        return new Date(raw).toLocaleString('en-GB', {
            timeZone: 'Europe/Malta',
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    } catch {
        return raw;
    }
};

const ListTickets = () => {

    const dateFormat = 'dd/mm/yy';
    const toast = useRef<Toast>(null);

    const [dateFrom, setDateFrom] = useState<Date>(new Date(dayjs().subtract(1, "month").toDate()));
    const [dateTo, setDateTo] = useState<Date>(new Date());
    const [deviceUserMap, setDeviceUserMap] = useState<Record<string, string>>({});
    const [rawItems, setRawItems] = useState<any[]>([]);
    const [data, setData] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [qrValue, setQrValue] = useState<string | null>(null);
    const [type] = useState("listTickets");
    const [sendEmailTicket, setSendEmailTicket] = useState<any | null>(null);
    const [sendEmailValue, setSendEmailValue] = useState("");
    const [sendEmailLoading, setSendEmailLoading] = useState(false);
    const [downloadingRef, setDownloadingRef] = useState<string | null>(null);
    const [emailSearch, setEmailSearch] = useState("");
    const [activeEmail, setActiveEmail] = useState("");
    const [useDateRange, setUseDateRange] = useState(true);

    const runEmailSearch = () => setActiveEmail(emailSearch.trim());

    const { session } = useSession();

    const setDate = (fieldName: string, value: Date) => {
        datePicker.max1Month({ fieldName, value, from: dateFrom, to: dateTo, setFrom: setDateFrom, setTo: setDateTo });
    };

    useEffect(() => {
        getData(session, `device-users`, `dropdown=true`)
            .then((users: { displayName: string; ref: string }[]) => {
                const map: Record<string, string> = {};
                users.forEach(u => { map[u.ref] = u.displayName; });
                setDeviceUserMap(map);
            })
            .catch(() => {});
    }, []);

    // Clearing the e-mail box drops the e-mail filter
    useEffect(() => {
        if (emailSearch.trim() === "") setActiveEmail("");
    }, [emailSearch]);

    useEffect(() => {
        // Date range and e-mail are independent filters: either one alone, or both together.
        const queryParts: string[] = [];
        if (useDateRange) {
            queryParts.push(
                `dateFrom=${dayjs(dateFrom).format("YYYY-MM-DD")}`,
                `dateTo=${dayjs(dateTo).format("YYYY-MM-DD")}`
            );
        }
        if (activeEmail) {
            queryParts.push(`email=${encodeURIComponent(activeEmail)}`);
        }

        if (queryParts.length === 0) {
            // Nothing to filter on: the API requires at least one filter
            setRawItems([]);
            setLoading(false);
            return;
        }

        setLoading(true);
        queryParts.push(`offset=0`, `limit=10000`);

        getData(session, "reports/list-tickets", queryParts.join("&"))
            .then((result: any) => {
                const sorted = (result?.items || [])
                    .sort((a: any, b: any) => {
                        const ta = a.soldAt ? new Date(a.soldAt).getTime() : 0;
                        const tb = b.soldAt ? new Date(b.soldAt).getTime() : 0;
                        return tb - ta;
                    });
                setRawItems(sorted);
            })
            .catch((err: any) => {
                toast.current?.show({ severity: 'error', summary: 'Error', detail: err.message });
            })
            .finally(() => setLoading(false));
    }, [dateFrom, dateTo, useDateRange, activeEmail]);

    useEffect(() => {
        setData(rawItems.map((item: any) => ({
            ...item,
            soldAt: formatSoldAt(item.soldAt),
            deviceUserRef: deviceUserMap[item.deviceUserRef] || item.deviceUserRef || 'N/A',
        })));
    }, [rawItems, deviceUserMap]);

    const customRowBodyTemplate = (rowData: any, options: any) => {
        const field = options.field;
        if (field === 'ref') {
            return rowData.ref
                ? <div
                    onClick={() => setQrValue(rowData.ref)}
                    title="Click to enlarge"
                    style={{ background: '#fff', padding: '6px', borderRadius: '6px', display: 'inline-block', boxShadow: '0 1px 4px rgba(0,0,0,0.10)', cursor: 'zoom-in' }}>
                    <QRCode value={rowData.ref} size={56} />
                  </div>
                : null;
        }
        const value = rowData[field];
        return (
            <span style={{
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                width: 26, height: 26, borderRadius: '50%',
                background: value ? '#dcfce7' : '#fee2e2'
            }}>
                <i className={classNames('pi', { 'pi-check text-green-600': value, 'pi-times text-red-500': !value })}
                   style={{ fontSize: 12, fontWeight: 700 }} />
            </span>
        );
    };

    const openSendEmailDialog = (rowData: any) => {
        setSendEmailTicket(rowData);
        setSendEmailValue(rowData.email || '');
    };

    const closeSendEmailDialog = () => {
        setSendEmailTicket(null);
        setSendEmailValue("");
    };

    const handleSendToEmail = () => {
        if (!sendEmailTicket || !sendEmailValue) return;

        setSendEmailLoading(true);
        postData(session, `tickets/resend`, { ref: sendEmailTicket.ref, email: sendEmailValue }, null)
            .then(() => {
                toast.current?.show({ severity: 'success', summary: `Ticket sent to ${sendEmailValue}` });
                closeSendEmailDialog();
            })
            .catch((err: any) => {
                toast.current?.show({ severity: 'error', summary: 'Error', detail: err.message });
            })
            .finally(() => setSendEmailLoading(false));
    };

    const handleDownloadPdf = (rowData: any) => {
        setDownloadingRef(rowData.ref);
        downloadFile(session, `tickets/pdf/${rowData.ref}`, null)
            .then((blob) => {
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = `ticket-${rowData.ref}.pdf`;
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(url);
            })
            .catch((err: any) => {
                toast.current?.show({ severity: 'error', summary: 'Error', detail: err.message });
            })
            .finally(() => setDownloadingRef(null));
    };

    const customButtons = [
        {
            id: 'send-email',
            icon: 'pi pi-envelope',
            onClick: openSendEmailDialog,
        },
        {
            id: 'download-pdf',
            icon: 'pi pi-file-pdf',
            onClick: handleDownloadPdf,
        },
    ];

    return (
        <div style={{ minHeight: '100vh', background: '#f1f5f9' }}>
            <Toast ref={toast} />

            {/* Send to another e-mail Dialog */}
            <Dialog
                visible={!!sendEmailTicket}
                onHide={closeSendEmailDialog}
                header="Send ticket to another e-mail"
                style={{ width: 380 }}
            >
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: '8px 0 4px' }}>
                    <label style={{ fontSize: 13, fontWeight: 600, color: '#475569' }}>E-Mail address</label>
                    <InputText
                        value={sendEmailValue}
                        onChange={(e) => setSendEmailValue(e.target.value)}
                        placeholder="name@example.com"
                    />
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 8 }}>
                        <Button label="Cancel" className="p-button-text" onClick={closeSendEmailDialog} disabled={sendEmailLoading} />
                        <Button label="Send" onClick={handleSendToEmail} loading={sendEmailLoading} disabled={!sendEmailValue} />
                    </div>
                </div>
            </Dialog>

            {/* QR Enlarge Dialog */}
            <Dialog
                visible={!!qrValue}
                onHide={() => setQrValue(null)}
                header="Ticket QR Code"
                style={{ width: 320 }}
            >
                {qrValue && (
                    <div style={{ display: 'flex', justifyContent: 'center', padding: '16px 0' }}>
                        <QRCode value={qrValue} size={240} />
                    </div>
                )}
            </Dialog>

            {/* Filter Card */}
            <div style={{ padding: '28px 28px 0' }}>
                <div style={{
                    background: '#fff',
                    borderRadius: 16,
                    boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
                    padding: '24px 28px',
                    display: 'flex',
                    flexWrap: 'wrap' as const,
                    gap: 20,
                    alignItems: 'flex-end'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#64748b', marginBottom: 4, width: '100%' }}>
                        <Filter size={15} color="#D72B63" />
                        <span style={{ fontSize: 12, fontWeight: 600, textTransform: 'uppercase' as const, letterSpacing: 0.8 }}>Filters</span>
                        <label style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, fontWeight: 600, color: '#475569', cursor: 'pointer' }}>
                            <Checkbox checked={useDateRange} onChange={(e) => setUseDateRange(!!e.checked)} />
                            Filter by date range
                        </label>
                    </div>

                    <div style={{ flex: '1 1 180px', minWidth: 160, opacity: useDateRange ? 1 : 0.45, pointerEvents: useDateRange ? 'auto' : 'none' }}>
                        <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#475569', marginBottom: 8, textTransform: 'uppercase' as const, letterSpacing: 0.6 }}>
                            <CalendarDays size={12} color="#D72B63" style={{ display: 'inline', marginRight: 5 }} />
                            From
                        </label>
                        <div style={{ border: '1.5px solid #e2e8f0', borderRadius: 8, padding: '4px 6px' }}>
                            <Calendar
                                dateFormat={dateFormat}
                                className="calendar"
                                value={dateFrom}
                                onChange={(e) => { if (e.value instanceof Date) setDate("from", e.value); }}
                                showIcon
                                showOnFocus={false}
                                readOnlyInput={false}
                                placeholder="dd/mm/yy"
                                style={{ width: '100%' }}
                            />
                        </div>
                    </div>

                    <div style={{ flex: '1 1 180px', minWidth: 160, opacity: useDateRange ? 1 : 0.45, pointerEvents: useDateRange ? 'auto' : 'none' }}>
                        <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#475569', marginBottom: 8, textTransform: 'uppercase' as const, letterSpacing: 0.6 }}>
                            <CalendarDays size={12} color="#D72B63" style={{ display: 'inline', marginRight: 5 }} />
                            To
                        </label>
                        <div style={{ border: '1.5px solid #e2e8f0', borderRadius: 8, padding: '4px 6px' }}>
                            <Calendar
                                dateFormat={dateFormat}
                                className="calendar"
                                value={dateTo}
                                onChange={(e) => { if (e.value instanceof Date) setDate("to", e.value); }}
                                showIcon
                                showOnFocus={false}
                                readOnlyInput={false}
                                placeholder="dd/mm/yy"
                                style={{ width: '100%' }}
                            />
                        </div>
                    </div>

                    <div style={{ flex: '1 1 220px', minWidth: 200 }}>
                        <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#475569', marginBottom: 8, textTransform: 'uppercase' as const, letterSpacing: 0.6 }}>
                            <Mail size={12} color="#D72B63" style={{ display: 'inline', marginRight: 5 }} />
                            E-Mail
                        </label>
                        <div style={{ display: 'flex', alignItems: 'stretch', gap: 8 }}>
                            <div style={{ flex: 1, border: '1.5px solid #e2e8f0', borderRadius: 8, padding: '4px 6px' }}>
                                <InputText
                                    value={emailSearch}
                                    onChange={(e) => setEmailSearch(e.target.value)}
                                    onKeyDown={(e) => { if (e.key === 'Enter') runEmailSearch(); }}
                                    placeholder="name@example.com"
                                    style={{ width: '100%', border: 'none', boxShadow: 'none' }}
                                />
                            </div>
                            <button
                                type="button"
                                onClick={runEmailSearch}
                                disabled={loading || emailSearch.trim() === ""}
                                title="Search by e-mail"
                                style={{
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    width: 44, border: 'none', borderRadius: 8,
                                    background: '#D72B63', color: '#fff',
                                    cursor: (loading || emailSearch.trim() === "") ? 'not-allowed' : 'pointer',
                                    opacity: (loading || emailSearch.trim() === "") ? 0.5 : 1
                                }}
                            >
                                <Search size={18} />
                            </button>
                        </div>
                        <p style={{ margin: '6px 0 0', fontSize: 11, color: '#94a3b8' }}>
                            {activeEmail
                                ? (useDateRange ? 'Filtering by e-mail within the date range' : 'Filtering by e-mail across all dates')
                                : (useDateRange ? 'Optional. Press the button or Enter to also filter by e-mail' : 'Date range is off. Enter an e-mail and press search')}
                        </p>
                    </div>

                </div>
            </div>

            {/* Table Section */}
            <div style={{ padding: '24px 28px 40px' }}>
                <div style={{
                    background: '#fff',
                    borderRadius: 16,
                    boxShadow: '0 2px 16px rgba(0,0,0,0.06)',
                    overflow: 'hidden'
                }}>
                    <div style={{
                        padding: '18px 24px',
                        borderBottom: '1px solid #f1f5f9',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between'
                    }}>
                        <div>
                            <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#1e293b' }}>Ticket Records</h2>
                            <p style={{ margin: '2px 0 0', fontSize: 13, color: '#94a3b8' }}>
                                {loading ? 'Loading...' : `${data.length} result${data.length !== 1 ? 's' : ''} found`}
                            </p>
                        </div>
                    </div>

                    <div className="table-container" style={{ padding: '8px 16px 16px' }}>
                        <Table
                            data={data}
                            type={type}
                            editButtonVisible={false}
                            addButtonVisible={false}
                            removeButtonVisible={false}
                            actionsVisible={true}
                            customButtons={customButtons}
                            shouldDisableButton={(rowData: any, buttonId: string) => buttonId === 'download-pdf' && downloadingRef === rowData.ref}
                            shouldRemoveButton={() => true}
                            exportButtonVisible={true}
                            map={models.listTicketsMap()}
                            customRowBodyTemplate={customRowBodyTemplate}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ListTickets;
